import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
  query,
  where,
  setDoc,
  Timestamp
} from "firebase/firestore";

// Colores por compañía (fallback si no viene color desde la BD)
const COMPANY_COLORS = {
  "Primera Compañía": "#ae2727ff",
  "Segunda Compañía": "#29b92bff",
  "Tercera Compañía": "#e74c3c",
  "Cuarta Compañía": "#f1c40f",
  "Quinta Compañía": "#8e44ad"
};

// Mapa de nombres de color (en español) -> código real
const COLOR_NOMBRE_DB = {
  Rojo: "#ae2727ff",
  "Rojo Oscuro": "#8B0000",
  Verde: "#29b92bff",
  Azul: "#3498db",
  Amarillo: "#f1c40f",
  Morado: "#8e44ad",
  Naranjo: "#e67e22",
  Celeste: "#5dade2",
  Blanco: "#ecf0f1",
  Negro: "#2c3e50"
};

function getCompanyColor(nombre) {
  return COMPANY_COLORS[nombre] || "#7f8c8d";
}

function normalizarColorDesdeBD(colorNombre, nombreCia) {
  if (typeof colorNombre === "string") {
    const limpio = colorNombre.trim();
    if (COLOR_NOMBRE_DB[limpio]) {
      return COLOR_NOMBRE_DB[limpio];
    }
  }
  // Si el color del bombero no existe en el mapa, usamos el de la compañía
  return getCompanyColor(nombreCia);
}

// Formatea hora desde Timestamp de Firestore o string "HH:MM"
function formatearHoraCampo(campo) {
  if (!campo) return null;

  if (campo.seconds) {
    const d = new Date(campo.seconds * 1000);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }

  if (typeof campo === "string") {
    return campo;
  }

  return null;
}

// Formatea FECHA + HORA desde Timestamp -> "dd-mm-aaaa HH:MM"
function formatearFechaHora(campo) {
  if (!campo) return null;

  if (campo.seconds) {
    const d = new Date(campo.seconds * 1000);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
  }

  if (typeof campo === "string") {
    return campo;
  }

  return null;
}

// Convierte Timestamp/string a Date (para comparaciones)
function obtenerDateDesdeCampo(campo) {
  if (!campo) return null;

  if (campo.seconds) {
    return new Date(campo.seconds * 1000);
  }

  if (typeof campo === "string") {
    const d = new Date(campo);
    if (!isNaN(d.getTime())) return d;
  }

  return null;
}

// Formatea lista de unidades autorizadas (array o string)
function formatearUnidades(unidades) {
  if (!unidades) return null;
  if (Array.isArray(unidades)) {
    if (unidades.length === 0) return null;
    return unidades.join(", ");
  }
  if (typeof unidades === "string") {
    const limpio = unidades.trim();
    return limpio || null;
  }
  return null;
}

// Normaliza RUT ingresado (acepta con o sin puntos/guion)
function normalizarRutEntrada(rut) {
  if (!rut) return "";
  let limpio = rut.replace(/\./g, "").replace(/\s+/g, "").toUpperCase();

  if (!limpio.includes("-") && limpio.length > 1) {
    const cuerpo = limpio.slice(0, -1);
    const dv = limpio.slice(-1);
    limpio = `${cuerpo}-${dv}`;
  }

  return limpio;
}

// Determina si una operadora está operativa según inicio/termino
function estaOperadoraActiva(operadora, ahora) {
  const inicio = obtenerDateDesdeCampo(operadora.inicioServicio);
  const termino = obtenerDateDesdeCampo(operadora.terminoServicio);

  if (!inicio) return false;
  if (inicio > ahora) return false;
  if (termino && termino <= ahora) return false;

  return true;
}

export default function VistaGlobal() {
  const { cuerpoId, cuerpo, cuerpoSlug } = useCuerpo();
  const navigate = useNavigate();
  const [bomberos, setBomberos] = useState([]);
  const [bomberosFuera, setBomberosFuera] = useState([]);
  const [operadoras, setOperadoras] = useState([]);

  const [companiaSeleccionada, setCompaniaSeleccionada] = useState(null);
  const [companiaFueraSeleccionada, setCompaniaFueraSeleccionada] =
    useState(null);

  // Estado para modal de operadoras
  const [mostrarModalOperadora, setMostrarModalOperadora] = useState(false);
  const [rutOperadora, setRutOperadora] = useState("");
  const [inicioServicio, setInicioServicio] = useState("");
  const [terminoServicio, setTerminoServicio] = useState("");
  const [errorOperadora, setErrorOperadora] = useState("");
  const [guardandoOperadora, setGuardandoOperadora] = useState(false);

  // Referencia temporal para actualizar estados automáticamente
  const [ahora, setAhora] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setAhora(new Date()), 60000); // cada 1 minuto
    return () => clearInterval(timer);
  }, []);

  // Cargar bomberos en servicio (filtrando vencidos)
  useEffect(() => {
    if (!cuerpoId) return;
    const colRef = query(
      collection(db, "bomberos_en_servicio"),
      where("cuerpo_id", "==", cuerpoId)
    );
    const unsub = onSnapshot(colRef, (snap) => {
      const ahora = new Date();
      const lista = [];

      snap.forEach((docSnap) => {
        const data = docSnap.data();
        const horaTermino = data.horaTerminoServicio?.seconds
          ? new Date(data.horaTerminoServicio.seconds * 1000)
          : null;

        // Solo considerar los que aún están dentro del horario
        if (!horaTermino || horaTermino > ahora) {
          lista.push({ id: docSnap.id, ...data });
        }
      });

      setBomberos(lista);
    });

    return () => unsub();
  }, [cuerpoId]);

  // Cargar bomberos fuera de servicio (0-8)
  useEffect(() => {
    if (!cuerpoId) return;
    const colRef = query(
      collection(db, "bomberos_fuera_servicio"),
      where("cuerpo_id", "==", cuerpoId)
    );
    const unsub = onSnapshot(colRef, (snap) => {
      const lista = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        lista.push({ id: docSnap.id, ...data });
      });
      setBomberosFuera(lista);
    });

    return () => unsub();
  }, [cuerpoId]);

  // Cargar operadoras (lista total)
  useEffect(() => {
    if (!cuerpoId) return;
    const colRef = query(
      collection(db, "operadoras"),
      where("cuerpo_id", "==", cuerpoId)
    );
    const unsub = onSnapshot(colRef, (snap) => {
      const lista = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        lista.push({ id: docSnap.id, ...data });
      });
      setOperadoras(lista);
    });

    return () => unsub();
  }, [cuerpoId]);

  // Agrupar por compañía y contar bomberos / maquinistas (EN SERVICIO)
  const resumenPorCompania = useMemo(() => {
    const grupos = {};

    for (const b of bomberos) {
      const nombreCia = b.compania || "Sin Compañía";

      if (!grupos[nombreCia]) {
        const colorNormalizado = normalizarColorDesdeBD(b.color, nombreCia);

        grupos[nombreCia] = {
          nombre: nombreCia,
          color: colorNormalizado,
          bomberos: [],
          totalBomberos: 0,
          totalMaquinistas: 0,
          totalNoMaquinistas: 0
        };
      }

      const grupo = grupos[nombreCia];

      if (b.color && grupo.color === getCompanyColor(nombreCia)) {
        grupo.color = normalizarColorDesdeBD(b.color, nombreCia);
      }

      grupo.bomberos.push(b);
      grupo.totalBomberos += 1;
      if (b.maquinista) {
        grupo.totalMaquinistas += 1;
      }
    }

    for (const key in grupos) {
      const g = grupos[key];
      g.totalNoMaquinistas = Math.max(
        0,
        g.totalBomberos - g.totalMaquinistas
      );
    }

    return grupos;
  }, [bomberos]);

  // Agrupar bomberos fuera de servicio por compañía
  const resumenFueraPorCompania = useMemo(() => {
    const grupos = {};

    for (const b of bomberosFuera) {
      if (b.visible === false) continue; // ocultar si visible = false

      const nombreCia = b.compania || "Sin Compañía";

      if (!grupos[nombreCia]) {
        const colorNormalizado = normalizarColorDesdeBD(b.color, nombreCia);

        grupos[nombreCia] = {
          nombre: nombreCia,
          color: colorNormalizado,
          bomberos: [],
          totalFuera: 0
        };
      }

      const grupo = grupos[nombreCia];

      if (b.color && grupo.color === getCompanyColor(nombreCia)) {
        grupo.color = normalizarColorDesdeBD(b.color, nombreCia);
      }

      grupo.bomberos.push(b);
      grupo.totalFuera += 1;
    }

    return grupos;
  }, [bomberosFuera]);

  const nombresCompanias = Object.keys(resumenPorCompania);
  const nombresFueraCompanias = Object.keys(resumenFueraPorCompania);

  const datosCompaniaSeleccionada =
    companiaSeleccionada && resumenPorCompania[companiaSeleccionada]
      ? resumenPorCompania[companiaSeleccionada]
      : null;

  const datosCompaniaFueraSeleccionada =
    companiaFueraSeleccionada &&
    resumenFueraPorCompania[companiaFueraSeleccionada]
      ? resumenFueraPorCompania[companiaFueraSeleccionada]
      : null;

  // Si la compañía seleccionada ya no tiene bomberos, limpiamos selección
  useEffect(() => {
    if (
      companiaSeleccionada &&
      !resumenPorCompania[companiaSeleccionada]
    ) {
      setCompaniaSeleccionada(null);
    }
  }, [resumenPorCompania, companiaSeleccionada]);

  useEffect(() => {
    if (
      companiaFueraSeleccionada &&
      !resumenFueraPorCompania[companiaFueraSeleccionada]
    ) {
      setCompaniaFueraSeleccionada(null);
    }
  }, [resumenFueraPorCompania, companiaFueraSeleccionada]);

  const hayBomberos = nombresCompanias.length > 0;
  const hayBomberosFuera = nombresFueraCompanias.length > 0;
  const hayOperadoras = operadoras.length > 0;

  // Guardar jornada de operadora
  const handleGuardarOperadora = async (e) => {
    e.preventDefault();
    setErrorOperadora("");

    const rutNorm = normalizarRutEntrada(rutOperadora);

    if (!rutNorm) {
      setErrorOperadora("Debes ingresar un RUT.");
      return;
    }

    if (!inicioServicio || !terminoServicio) {
      setErrorOperadora(
        "Debes ingresar fecha y hora de inicio y término de la jornada."
      );
      return;
    }

    const fechaInicio = new Date(inicioServicio);
    const fechaTermino = new Date(terminoServicio);

    if (isNaN(fechaInicio.getTime()) || isNaN(fechaTermino.getTime())) {
      setErrorOperadora("Las fechas ingresadas no son válidas.");
      return;
    }

    if (fechaTermino <= fechaInicio) {
      setErrorOperadora(
        "La fecha/hora de término debe ser posterior a la de inicio."
      );
      return;
    }

    try {
      setGuardandoOperadora(true);

      const docRef = doc(db, "operadoras", rutNorm);
      const snap = await getDoc(docRef);

      if (!snap.exists() || snap.data()?.cuerpo_id !== cuerpoId) {
        setErrorOperadora(
          "El RUT ingresado no está registrado como operadora."
        );
        setGuardandoOperadora(false);
        return;
      }

      await setDoc(
        docRef,
        {
          rut: rutNorm,
          inicioServicio: Timestamp.fromDate(fechaInicio),
          terminoServicio: Timestamp.fromDate(fechaTermino),
          actualizadoEn: Timestamp.now()
        },
        { merge: true }
      );

      setRutOperadora("");
      setInicioServicio("");
      setTerminoServicio("");
      setMostrarModalOperadora(false);
    } catch (err) {
      console.error("Error guardando jornada de operadora:", err);
      setErrorOperadora("Ocurrió un error al guardar. Intenta nuevamente.");
    } finally {
      setGuardandoOperadora(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "24px 16px 40px",
        boxSizing: "border-box",
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
      }}
    >
      {/* Fila superior: botón Ingreso Operadoras + título centrado */}
      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px"
        }}
      >
        <button
          onClick={() => {
            setMostrarModalOperadora(true);
            setErrorOperadora("");
          }}
          style={{
            padding: "8px 14px",
            borderRadius: "999px",
            border: "none",
            cursor: "pointer",
            background: "#2c3e50",
            color: "#ffffff",
            fontSize: "0.9rem",
            fontWeight: 600,
            boxShadow: "0 2px 6px rgba(0,0,0,0.25)"
          }}
        >
          Ingreso Operadoras
        </button>

        <div
          style={{
            flex: 1,
            textAlign: "center"
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: "1.2rem",
              textTransform: "uppercase"
            }}
          >
            BIENVENIDO A LA PLATAFORMA WEB DE GESTIÓN DE BOMBEROS EN SERVICIO
          </div>
        </div>

        <button
          onClick={() => navigate(`/${cuerpoSlug}/partes`)}
          style={{
            padding: "8px 14px",
            borderRadius: "999px",
            border: "none",
            cursor: "pointer",
            background: "#ae2727ff",
            color: "#ffffff",
            fontSize: "0.9rem",
            fontWeight: 600,
            boxShadow: "0 2px 6px rgba(0,0,0,0.25)"
          }}
        >
          Ir a Partes
        </button>
      </div>

      {/* Panel superior: tarjetas por compañía (EN SERVICIO) */}
      {hayBomberos ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            justifyContent: "center",
            marginBottom: "24px",
            width: "100%",
            maxWidth: "1200px"
          }}
        >
          {nombresCompanias.map((nombreCia) => {
            const info = resumenPorCompania[nombreCia];
            const color = info.color || getCompanyColor(nombreCia);
            const seleccionada = companiaSeleccionada === nombreCia;

            return (
              <button
                key={nombreCia}
                onClick={() =>
                  setCompaniaSeleccionada(
                    seleccionada ? null : nombreCia
                  )
                }
                style={{
                  border: "none",
                  cursor: "pointer",
                  background: "transparent",
                  padding: 0
                }}
              >
                <div
                  style={{
                    minWidth: "220px",
                    padding: "12px 18px",
                    borderRadius: "14px",
                    background: color,
                    color: "#ffffff",
                    boxShadow: seleccionada
                      ? "0 0 0 4px rgba(0,0,0,0.35)"
                      : "0 4px 12px rgba(0,0,0,0.25)",
                    transition:
                      "transform 0.15s ease, box-shadow 0.15s ease",
                    transform: seleccionada ? "translateY(-2px)" : "none"
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      marginBottom: 8,
                      textAlign: "center"
                    }}
                  >
                    {nombreCia}
                  </div>
                  <div style={{ textAlign: "left", fontSize: "0.95rem" }}>
                    <div>
                      👨‍🚒 {info.totalNoMaquinistas} Bomberos
                    </div>
                    <div>
                      🚒 {info.totalMaquinistas} Conductores
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            marginBottom: "24px",
            padding: "12px 18px",
            borderRadius: 10,
            background: "#f5f5f5",
            color: "#444",
            fontSize: "1rem",
            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
            textAlign: "center"
          }}
        >
          Actualmente ninguna compañía tiene bomberos en servicio.
        </div>
      )}

      {/* Detalle de compañía seleccionada (EN SERVICIO) */}
      {datosCompaniaSeleccionada && (
        <div
          style={{
            width: "100%",
            maxWidth: "1200px",
            marginBottom: "32px"
          }}
        >
          {/* Encabezado de la compañía */}
          <div
            style={{
              width: "100%",
              padding: "16px 20px",
              borderRadius: "14px",
              background: datosCompaniaSeleccionada.color,
              color: "#ffffff",
              textAlign: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              marginBottom: "16px"
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: "1.2rem",
                marginBottom: 6
              }}
            >
              {datosCompaniaSeleccionada.nombre}
            </div>
            <div style={{ fontSize: "0.95rem" }}>
              <div>
                👨‍🚒 {datosCompaniaSeleccionada.totalNoMaquinistas} Bomberos
              </div>
              <div>
                🚒 {datosCompaniaSeleccionada.totalMaquinistas} Conductores
              </div>
            </div>
          </div>

          {/* Grid de bomberos en servicio de esa compañía */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              justifyContent: "center"
            }}
          >
            {datosCompaniaSeleccionada.bomberos.map((b) => {
              const horaInicio = formatearHoraCampo(b.horaInicioServicio);
              const horaTermino = formatearHoraCampo(
                b.horaTerminoServicio
              );
              const unidades = formatearUnidades(b.unidades);

              return (
                <div
                  key={b.id}
                  style={{
                    width: "150px",
                    borderRadius: "12px",
                    overflow: "hidden",
                    background: "#fdfdfd",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.18)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch"
                  }}
                >
                  {b.fotoURL ? (
                    <img
                      src={b.fotoURL}
                      alt={b.nombre}
                      style={{
                        width: "100%",
                        height: "180px",
                        objectFit: "cover",
                        background: "#ddd"
                      }}
                      loading="lazy"
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "180px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#eeeeee",
                        color: "#777",
                        fontSize: "0.8rem"
                      }}
                    >
                      SIN FOTO
                    </div>
                  )}

                  <div
                    style={{
                      padding: "8px 10px 10px",
                      textAlign: "center",
                      fontSize: "0.85rem",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        minHeight: "2.0em"
                      }}
                    >
                      {b.nombre}
                    </div>

                    <div
                      style={{
                        color: "#555",
                        minHeight: "1.6em"
                      }}
                    >
                      {b.cargo || ""}
                    </div>

                    {b.maquinista && (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: "0.82rem",
                          lineHeight: 1.2
                        }}
                      >
                        🚒 <strong>CONDUCTOR</strong>
                        {unidades && (
                          <div style={{ marginTop: 2 }}>
                            Unidades: {unidades}
                          </div>
                        )}
                      </div>
                    )}

                    {b.ubicacion && (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: "0.8rem",
                          color: "#444"
                        }}
                      >
                        Ubicación: {b.ubicacion}
                      </div>
                    )}

                    {(horaInicio || horaTermino) && (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: "0.8rem",
                          color: "#555"
                        }}
                      >
                        {horaInicio && <>Inicio: {horaInicio}</>}
                        {horaInicio && horaTermino && <br />}
                        {horaTermino && <>Término: {horaTermino}</>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sección Bomberos Fuera de Servicio (0-8) */}
      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          marginTop: "24px",
          marginBottom: "8px",
          textAlign: "center",
          fontWeight: 700,
          fontSize: "1rem"
        }}
      >
        Bomberos Fuera de Servicio (0-8)
      </div>

      {hayBomberosFuera ? (
        <>
          {/* Rectángulos por compañía (fuera de servicio) */}
          <div
            style={{
              width: "100%",
              maxWidth: "1200px",
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              justifyContent: "center",
              marginBottom: "16px"
            }}
          >
            {nombresFueraCompanias.map((nombreCia) => {
              const info = resumenFueraPorCompania[nombreCia];
              const color = info.color || getCompanyColor(nombreCia);
              const seleccionada =
                companiaFueraSeleccionada === nombreCia;

              return (
                <button
                  key={nombreCia}
                  onClick={() =>
                    setCompaniaFueraSeleccionada(
                      seleccionada ? null : nombreCia
                    )
                  }
                  style={{
                    border: "none",
                    cursor: "pointer",
                    background: "transparent",
                    padding: 0
                  }}
                >
                  <div
                    style={{
                      minWidth: "220px",
                      padding: "10px 16px",
                      borderRadius: "14px",
                      background: color,
                      color: "#ffffff",
                      boxShadow: seleccionada
                        ? "0 0 0 4px rgba(0,0,0,0.35)"
                        : "0 4px 12px rgba(0,0,0,0.25)",
                      transition:
                        "transform 0.15s ease, box-shadow 0.15s ease",
                      transform: seleccionada ? "translateY(-2px)" : "none"
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        marginBottom: 6,
                        textAlign: "center"
                      }}
                    >
                      {nombreCia}
                    </div>
                    <div style={{ fontSize: "0.9rem" }}>
                      👨‍🚒 {info.totalFuera} Bomberos 0-8
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Detalle desplegable de compañía fuera de servicio */}
          {datosCompaniaFueraSeleccionada && (
            <div
              style={{
                width: "100%",
                maxWidth: "800px",
                marginBottom: "32px"
              }}
            >
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "14px",
                  background: datosCompaniaFueraSeleccionada.color,
                  color: "#ffffff",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                  marginBottom: "12px"
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "1.1rem"
                  }}
                >
                  {datosCompaniaFueraSeleccionada.nombre}
                </div>
                <div style={{ fontSize: "0.9rem", marginTop: 4 }}>
                  👨‍🚒 {datosCompaniaFueraSeleccionada.totalFuera} Bomberos
                  fuera de servicio (0-8)
                </div>
              </div>

              <div
                style={{
                  padding: "14px 16px 16px",
                  borderRadius: "14px",
                  background: datosCompaniaFueraSeleccionada.color,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.25)"
                }}
              >
                {datosCompaniaFueraSeleccionada.bomberos
                  .filter((b) => b.visible !== false)
                  .map((b) => {
                    const desde = formatearFechaHora(b.fecha_inicio);
                    const hasta = formatearFechaHora(b.fecha_fin);
                    const motivo =
                      b.motivo || "Sin motivo especificado";

                    return (
                      <div
                        key={b.id}
                        style={{
                          background: "rgba(255,255,255,0.12)",
                          borderRadius: 10,
                          padding: "8px 10px",
                          fontSize: "0.9rem",
                          marginBottom: 8,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 8
                        }}
                      >
                        {/* Foto pequeña o SIN FOTO */}
                        {b.fotoURL ? (
                          <img
                            src={b.fotoURL}
                            alt={b.nombre}
                            style={{
                              width: "50px",
                              height: "65px",
                              objectFit: "cover",
                              borderRadius: 8,
                              background: "#ddd",
                              flexShrink: 0
                            }}
                            loading="lazy"
                          />
                        ) : (
                          <div
                            style={{
                              width: "50px",
                              height: "65px",
                              borderRadius: 8,
                              background: "rgba(255,255,255,0.25)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.6rem",
                              textAlign: "center",
                              flexShrink: 0
                            }}
                          >
                            SIN
                            <br />
                            FOTO
                          </div>
                        )}

                        {/* Texto */}
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: "0.95rem",
                              marginBottom: 2
                            }}
                          >
                            {b.nombre}
                          </div>

                          {b.cargo && (
                            <div
                              style={{
                                fontSize: "0.85rem",
                                marginTop: 2
                              }}
                            >
                              Cargo: {b.cargo}
                            </div>
                          )}

                          {(desde || hasta) && (
                            <div
                              style={{
                                fontSize: "0.85rem",
                                marginTop: 2
                              }}
                            >
                              {desde && <>Desde: {desde}</>}
                              {desde && hasta && <br />}
                              {hasta && <>Hasta: {hasta}</>}
                            </div>
                          )}

                          {motivo && (
                            <div
                              style={{
                                fontSize: "0.85rem",
                                marginTop: 4
                              }}
                            >
                              Motivo: {motivo}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            marginBottom: "24px",
            padding: "10px 16px",
            borderRadius: 10,
            background: "#f5f5f5",
            color: "#444",
            textAlign: "center",
            maxWidth: "600px"
          }}
        >
          Actualmente no existen bomberos fuera de servicio (0-8)
        </div>
      )}

      {/* Tabla Operadoras */}
      <div
        style={{
          width: "100%",
          maxWidth: "900px",
          marginTop: "24px",
          marginBottom: "24px"
        }}
      >
        <h3
          style={{
            textAlign: "center",
            marginBottom: "12px",
            fontSize: "1rem"
          }}
        >
          Operadoras
        </h3>

        {hayOperadoras ? (
          <div
            style={{
              width: "100%",
              overflowX: "auto",
              borderRadius: 10,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.9rem",
                background: "#ffffff"
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#ecf0f1"
                  }}
                >
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd"
                    }}
                  >
                    Estado
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd"
                    }}
                  >
                    Nombre
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd"
                    }}
                  >
                    Inicio Jornada
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd"
                    }}
                  >
                    Término Jornada
                  </th>
                </tr>
              </thead>
              <tbody>
                {operadoras.map((op) => {
                  const activa = estaOperadoraActiva(op, ahora);
                  const nombreOp =
                    op.nombreCompleto || op.nombre || op.id;
                  const inicioFmt = activa
                    ? formatearFechaHora(op.inicioServicio)
                    : "";
                  const terminoFmt = activa
                    ? formatearFechaHora(op.terminoServicio)
                    : "";

                  return (
                    <tr key={op.id}>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid #f0f0f0"
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            marginRight: "6px",
                            backgroundColor: activa ? "#2ecc71" : "#e74c3c"
                          }}
                        />
                        {activa ? "Operativa" : "No operativa"}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid #f0f0f0"
                        }}
                      >
                        {nombreOp}
                      </td>
                      
                      <td
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid #f0f0f0"
                        }}
                      >
                        {inicioFmt || "—"}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid #f0f0f0"
                        }}
                      >
                        {terminoFmt || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              fontSize: "0.9rem",
              color: "#555"
            }}
          >
            Aún no hay operadoras registradas.
          </div>
        )}
      </div>

      {/* Logo institucional del Cuerpo actual */}
      <div style={{ marginTop: "16px", textAlign: "center" }}>
        {(() => {
          const remoto = String(
            cuerpo?.logoURL || cuerpo?.logo_url || cuerpo?.logoUrl || cuerpo?.logo || ""
          ).trim();
          const fallbackHuasco =
            String(cuerpoId || "").toLowerCase() === "huasco"
              ? "/logos/logo-cuerpo.png"
              : "";
          const logo = remoto || fallbackHuasco;

          if (!logo) {
            return (
              <div style={{ color: "#666", fontSize: "0.95rem", padding: "18px 0" }}>
                Logo institucional no configurado
              </div>
            );
          }

          return (
            <img
              src={logo}
              alt={cuerpo?.nombre || "Cuerpo de Bomberos"}
              style={{
                maxWidth: "260px",
                width: "60vw",
                height: "auto",
                display: "block",
                margin: "0 auto"
              }}
              loading="lazy"
            />
          );
        })()}
      </div>

      {/* Modal Ingreso Operadoras */}
      {mostrarModalOperadora && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "400px",
              background: "#ffffff",
              borderRadius: "14px",
              padding: "16px 18px 18px",
              boxShadow: "0 6px 20px rgba(0,0,0,0.35)"
            }}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "10px",
                textAlign: "center",
                fontSize: "1rem"
              }}
            >
              Ingreso de Operadora
            </h3>

            <form onSubmit={handleGuardarOperadora}>
              <div style={{ marginBottom: "8px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.85rem",
                    marginBottom: "2px"
                  }}
                >
                  RUT
                </label>
                <input
                  type="text"
                  value={rutOperadora}
                  onChange={(e) => setRutOperadora(e.target.value)}
                  placeholder="Ej: 12.345.678-9"
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                    fontSize: "0.9rem"
                  }}
                />
              </div>

              <div style={{ marginBottom: "8px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.85rem",
                    marginBottom: "2px"
                  }}
                >
                  Inicio de jornada
                </label>
                <input
                  type="datetime-local"
                  value={inicioServicio}
                  onChange={(e) => setInicioServicio(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                    fontSize: "0.9rem"
                  }}
                />
              </div>

              <div style={{ marginBottom: "8px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.85rem",
                    marginBottom: "2px"
                  }}
                >
                  Término de jornada
                </label>
                <input
                  type="datetime-local"
                  value={terminoServicio}
                  onChange={(e) => setTerminoServicio(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                    fontSize: "0.9rem"
                  }}
                />
              </div>

              {errorOperadora && (
                <div
                  style={{
                    color: "#c0392b",
                    fontSize: "0.8rem",
                    marginBottom: "8px"
                  }}
                >
                  {errorOperadora}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                  marginTop: "10px"
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setMostrarModalOperadora(false);
                    setErrorOperadora("");
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "999px",
                    border: "none",
                    cursor: "pointer",
                    background: "#bdc3c7",
                    color: "#2c3e50",
                    fontSize: "0.85rem"
                  }}
                >
                  Cerrar
                </button>
                <button
                  type="submit"
                  disabled={guardandoOperadora}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "999px",
                    border: "none",
                    cursor: "pointer",
                    background: "#27ae60",
                    color: "#ffffff",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    opacity: guardandoOperadora ? 0.7 : 1
                  }}
                >
                  {guardandoOperadora ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
